import type { Action } from "./actions";
import type { Attachment } from "./attachments";
import type { Comment } from "./comments";
import type { RootCause } from "./root-causes";
import type { Step } from "./steps";
import type { TeamMember } from "./team-members";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SUPPLIER_PORTAL_FUNCTION = "supplier-8d";

export interface SupplierClaim {
  id: string;
  tenant_id: string;
  factory_id: string;
  supplier_id: string;
  claim_number: string;
  title: string;
  status: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  magic_token_expires_at: string;
  supplier?: { name: string; contact_email?: string | null } | null;
  factory?: { name: string } | null;
  originator?: { full_name: string | null; email: string } | null;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: Record<string, unknown>;
  query?: Record<string, string | number | null | undefined>;
}

export interface SupplierAttachmentUploadTarget {
  path: string;
  token: string;
}

function buildUrl(path: string, token: string, query?: RequestOptions["query"]) {
  const url = new URL(`${SUPABASE_URL}/functions/v1/${SUPPLIER_PORTAL_FUNCTION}${path}`);
  url.searchParams.set("token", token);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
}

async function request<T>(path: string, token: string, options: RequestOptions = {}): Promise<T> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error("Missing Supabase configuration");
  }

  const response = await fetch(buildUrl(path, token, options.query), {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const payload = await response.json();
      if (payload?.error) message = payload.error;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export function createSupplierPortalClient(token: string) {
  return {
    fetchClaim: () => request<SupplierClaim>("/claim", token),
    updateClaimStatus: (status: string) => request("/claim", token, { method: "PATCH", body: { status } }),
    fetchSteps: () => request<Step[]>("/steps", token),
    updateStepByNumber: (stepNumber: number, content: string, status: string) =>
      request("/steps", token, { method: "PATCH", body: { step_number: stepNumber, content, status } }),
    fetchComments: () => request<Comment[]>("/comments", token),
    createComment: (content: string, authorName: string) =>
      request<Comment>("/comments", token, { method: "POST", body: { content, author_name: authorName } }),
    fetchActions: (stepNumber?: number) =>
      request<Action[]>("/actions", token, { query: { step: stepNumber ?? null } }),
    createAction: (input: { step_number: number; description: string; responsible?: string | null; due_date?: string | null }) =>
      request<Action>("/actions", token, { method: "POST", body: input }),
    updateActionStatus: (id: string, status: string) =>
      request("/actions", token, { method: "PATCH", body: { id, status } }),
    deleteAction: (id: string) => request("/actions", token, { method: "DELETE", query: { id } }),
    fetchTeamMembers: () => request<TeamMember[]>("/team-members", token),
    createTeamMember: (input: { name: string; role: string; email?: string | null }) =>
      request<TeamMember>("/team-members", token, { method: "POST", body: input }),
    deleteTeamMember: (id: string) => request("/team-members", token, { method: "DELETE", query: { id } }),
    fetchRootCauses: (method?: string) => request<RootCause[] | RootCause | null>("/root-causes", token, { query: { method: method ?? null } }),
    upsertRootCause: (method: string, data: RootCause["data"]) =>
      request<RootCause>("/root-causes", token, { method: "PUT", body: { method, data } }),
    fetchAttachments: () => request<Attachment[]>("/attachments", token),
    createAttachmentUploadUrl: (fileName: string) =>
      request<SupplierAttachmentUploadTarget>("/attachments/upload-url", token, {
        method: "POST",
        body: { file_name: fileName },
      }),
    createAttachment: (input: Omit<Attachment, "id" | "tenant_id" | "claim_id" | "created_at">) =>
      request<Attachment>("/attachments", token, { method: "POST", body: input }),
    deleteAttachment: (id: string) => request("/attachments", token, { method: "DELETE", query: { id } }),
  };
}
